import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { fetchParkingById } from '../store/parkingSlice';
import { createBooking } from '../store/bookingSlice';

const VEHICLE_TYPES = ['Car', 'Bike', 'SUV', 'Bus'];

export default function BookingPage() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { selected: parking } = useSelector(s => s.parking);
  const { loading, currentBooking } = useSelector(s => s.booking);
  const { user } = useSelector(s => s.auth);

  const [step, setStep] = useState(1); // 1: Details, 2: Confirm, 3: Success
  const [form, setForm] = useState({
    vehicleNumber: '',
    vehicleType: 'Car',
    duration: 2,
    startTime: new Date(Date.now() + 15 * 60000).toISOString().slice(0, 16),
    paymentMethod: 'online',
  });

  useEffect(() => {
    dispatch(fetchParkingById(id));
  }, [dispatch, id]);

  const totalAmount = parking ? parking.pricePerHour * form.duration : 0;
  const endTime = new Date(new Date(form.startTime).getTime() + form.duration * 3600000);

  const handleBook = async () => {
    const result = await dispatch(createBooking({
      parkingId: id,
      ...form,
      endTime: endTime.toISOString(),
      totalAmount,
    }));
    if (result.payload?.booking || result.payload?._id) {
      setStep(3);
    }
  };

  if (!parking) {
    return (
      <div style={{ paddingTop: '64px', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#94a3b8', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚗</div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const inputStyle = {
    width: '100%', padding: '13px 16px',
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px', color: '#fff', fontSize: '15px', outline: 'none',
  };

  return (
    <div style={{ paddingTop: '64px', minHeight: '100vh', padding: '80px 24px 40px' }}>
      <div style={{ maxWidth: '680px', margin: '0 auto' }}>
        {/* Header */}
        <button onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} style={{
          background: 'none', border: 'none', color: '#6366f1', cursor: 'pointer',
          fontSize: '14px', fontWeight: '600', marginBottom: '24px',
        }}>
          ← {step > 1 ? 'Back' : 'Cancel'}
        </button>

        {/* Progress Steps */}
        {step < 3 && (
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '32px', gap: '8px' }}>
            {['Booking Details', 'Confirm & Pay', 'Done!'].map((label, i) => (
              <React.Fragment key={i}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '12px', fontWeight: '800',
                    background: i + 1 <= step ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(255,255,255,0.1)',
                    color: i + 1 <= step ? '#fff' : '#64748b',
                  }}>{i + 1}</div>
                  <span style={{ fontSize: '12px', color: i + 1 === step ? '#fff' : '#64748b', fontWeight: i + 1 === step ? '700' : '400' }}>{label}</span>
                </div>
                {i < 2 && <div style={{ flex: 1, height: 1, background: i + 1 < step ? '#6366f1' : 'rgba(255,255,255,0.1)' }} />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Parking Summary Card */}
        {step < 3 && (
          <div style={{
            background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: '16px', padding: '16px', marginBottom: '24px',
            display: 'flex', gap: '16px', alignItems: 'center',
          }}>
            <img
              src={parking.images?.[0] || 'https://images.unsplash.com/photo-1506521781263-d8422e82f27a?w=200'}
              alt={parking.name}
              style={{ width: 80, height: 60, borderRadius: '10px', objectFit: 'cover' }}
            />
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '15px', fontWeight: '700', marginBottom: '4px' }}>{parking.name}</h3>
              <p style={{ color: '#64748b', fontSize: '12px' }}>📍 {parking.address}</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '20px', fontWeight: '800', color: '#6366f1' }}>₹{parking.pricePerHour}</div>
              <div style={{ fontSize: '11px', color: '#64748b' }}>per hour</div>
            </div>
          </div>
        )}

        {/* Step 1: Booking Details */}
        <AnimatePresence mode="wait">
          {step === 1 && (
            <motion.div key="step1" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '28px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '24px' }}>Booking Details</h2>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px' }}>Vehicle Number</label>
                  <input
                    value={form.vehicleNumber}
                    onChange={e => setForm({ ...form, vehicleNumber: e.target.value.toUpperCase() })}
                    placeholder="MH 20 AB 1234"
                    style={inputStyle}
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px' }}>Vehicle Type</label>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {VEHICLE_TYPES.map(v => (
                      <button
                        key={v}
                        onClick={() => setForm({ ...form, vehicleType: v })}
                        style={{
                          padding: '10px 20px', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '600',
                          background: form.vehicleType === v ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${form.vehicleType === v ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.1)'}`,
                          color: form.vehicleType === v ? '#6366f1' : '#94a3b8',
                        }}
                      >
                        {v === 'Car' ? '🚗' : v === 'Bike' ? '🏍️' : v === 'SUV' ? '🚙' : '🚌'} {v}
                      </button>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px' }}>Start Time</label>
                  <input
                    type="datetime-local"
                    value={form.startTime}
                    onChange={e => setForm({ ...form, startTime: e.target.value })}
                    min={new Date().toISOString().slice(0, 16)}
                    style={{ ...inputStyle, colorScheme: 'dark' }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '12px' }}>
                    Duration: <span style={{ color: '#6366f1' }}>{form.duration} hour{form.duration > 1 ? 's' : ''}</span>
                  </label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[1, 2, 3, 4, 6, 8, 12, 24].map(h => (
                      <button
                        key={h}
                        onClick={() => setForm({ ...form, duration: h })}
                        style={{
                          padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                          background: form.duration === h ? 'rgba(99,102,241,0.2)' : 'rgba(255,255,255,0.05)',
                          border: `1px solid ${form.duration === h ? 'rgba(99,102,241,0.5)' : 'rgba(255,255,255,0.1)'}`,
                          color: form.duration === h ? '#6366f1' : '#94a3b8',
                        }}
                      >
                        {h}h
                      </button>
                    ))}
                  </div>
                </div>

                {/* Amount Preview */}
                <div style={{ background: 'rgba(99,102,241,0.08)', borderRadius: '12px', padding: '16px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#94a3b8', fontSize: '13px' }}>₹{parking.pricePerHour} × {form.duration}h</span>
                    <span style={{ fontWeight: '700' }}>₹{totalAmount}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
                    <span style={{ fontWeight: '700' }}>Total</span>
                    <span style={{ fontSize: '20px', fontWeight: '900', color: '#6366f1' }}>₹{totalAmount}</span>
                  </div>
                </div>

                <button
                  onClick={() => setStep(2)}
                  disabled={!form.vehicleNumber}
                  style={{
                    width: '100%', padding: '16px',
                    background: !form.vehicleNumber ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    color: '#fff', border: 'none', borderRadius: '14px',
                    fontSize: '16px', fontWeight: '800', cursor: !form.vehicleNumber ? 'not-allowed' : 'pointer',
                  }}
                >
                  Continue to Payment →
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Confirm */}
          {step === 2 && (
            <motion.div key="step2" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px', padding: '28px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '800', marginBottom: '24px' }}>Confirm Booking</h2>

                {/* Summary */}
                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '14px', padding: '20px', marginBottom: '20px' }}>
                  {[
                    { label: 'Parking', value: parking.name },
                    { label: 'Vehicle', value: `${form.vehicleType} - ${form.vehicleNumber}` },
                    { label: 'Start Time', value: new Date(form.startTime).toLocaleString('en-IN') },
                    { label: 'End Time', value: endTime.toLocaleString('en-IN') },
                    { label: 'Duration', value: `${form.duration} hour${form.duration > 1 ? 's' : ''}` },
                  ].map((item, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: i < 4 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                      <span style={{ color: '#64748b', fontSize: '13px' }}>{item.label}</span>
                      <span style={{ fontWeight: '600', fontSize: '13px', textAlign: 'right', maxWidth: '60%' }}>{item.value}</span>
                    </div>
                  ))}
                </div>

                {/* Payment Method */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#94a3b8', marginBottom: '12px' }}>Payment Method</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {[
                      { value: 'online', label: '💳 Online (UPI/Card)', desc: 'Pay now securely' },
                      { value: 'cash', label: '💵 Cash at Gate', desc: 'Pay when you arrive' },
                    ].map(pm => (
                      <button
                        key={pm.value}
                        onClick={() => setForm({ ...form, paymentMethod: pm.value })}
                        style={{
                          flex: 1, padding: '14px', borderRadius: '12px', cursor: 'pointer', textAlign: 'left',
                          background: form.paymentMethod === pm.value ? 'rgba(99,102,241,0.15)' : 'rgba(255,255,255,0.03)',
                          border: `1px solid ${form.paymentMethod === pm.value ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.08)'}`,
                        }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: '700', marginBottom: '4px', color: form.paymentMethod === pm.value ? '#6366f1' : '#fff' }}>{pm.label}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>{pm.desc}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.1))', border: '1px solid rgba(99,102,241,0.2)', borderRadius: '14px', padding: '20px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '16px', fontWeight: '700' }}>Total Amount</span>
                    <span style={{ fontSize: '28px', fontWeight: '900', color: '#6366f1' }}>₹{totalAmount}</span>
                  </div>
                </div>

                <button
                  onClick={handleBook}
                  disabled={loading}
                  style={{
                    width: '100%', padding: '16px',
                    background: loading ? 'rgba(99,102,241,0.5)' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                    color: '#fff', border: 'none', borderRadius: '14px',
                    fontSize: '16px', fontWeight: '800', cursor: loading ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? 'Processing...' : form.paymentMethod === 'online' ? '💳 Pay ₹' + totalAmount : '✓ Confirm Booking'}
                </button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Success */}
          {step === 3 && (
            <motion.div key="step3" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(16,185,129,0.3)', borderRadius: '20px', padding: '48px', textAlign: 'center' }}>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.2 }}
                  style={{
                    width: 80, height: 80, borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '36px', margin: '0 auto 24px',
                  }}
                >✓</motion.div>

                <h2 style={{ fontSize: '28px', fontWeight: '900', marginBottom: '12px' }}>Booking Confirmed!</h2>
                <p style={{ color: '#94a3b8', marginBottom: '32px', lineHeight: 1.6 }}>
                  Your parking slot at <strong style={{ color: '#fff' }}>{parking.name}</strong> has been booked successfully.
                </p>

                {/* QR Code Placeholder */}
                <div style={{
                  width: 160, height: 160, margin: '0 auto 24px',
                  background: '#fff', borderRadius: '16px', padding: '12px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <div style={{ fontSize: '12px', color: '#000', textAlign: 'center', fontWeight: '700' }}>
                    <div style={{ fontSize: '48px', marginBottom: '8px' }}>📱</div>
                    QR Code<br />
                    <span style={{ fontSize: '10px', color: '#666' }}>Show at gate</span>
                  </div>
                </div>

                <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: '12px', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ color: '#64748b', fontSize: '13px' }}>Booking ID</span>
                    <span style={{ fontWeight: '700', fontSize: '13px', color: '#6366f1' }}>
                      #{currentBooking?._id?.slice(-8)?.toUpperCase() || 'SP' + Date.now().toString().slice(-6)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b', fontSize: '13px' }}>Amount Paid</span>
                    <span style={{ fontWeight: '700', fontSize: '13px' }}>₹{totalAmount}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => navigate('/dashboard')}
                    style={{
                      flex: 1, padding: '14px',
                      background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
                      color: '#fff', border: 'none', borderRadius: '12px',
                      fontSize: '15px', fontWeight: '700', cursor: 'pointer',
                    }}
                  >
                    View Dashboard
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    style={{
                      flex: 1, padding: '14px',
                      background: 'rgba(255,255,255,0.05)', color: '#94a3b8',
                      border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
                      fontSize: '15px', cursor: 'pointer',
                    }}
                  >
                    Go Home
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}